import { HttpParameterCodec, HttpClient } from '@angular/common/http';
import { Injectable, Optional, Inject } from '@angular/core';
import { EncryptService } from '@app/shared/services/encrypt.service';
import { SessionStorageService } from '@app/shared/services/session-storage.service';
import { BehaviorSubject, EMPTY, from, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Configuration } from '@app/common/configuration';
import { environment } from '../../../environments/environment';
import { BASE_PATH } from '@app/common/variables';
import { CustomHttpParameterCodec } from '@app/common/encoder';
import { TokenManagementService } from './token-management.service';
import { TokenResponseDto } from './TokenResponseDto';
import { Router } from '@angular/router';
import { AppSessionService } from './app-session.service';
import { UserService } from './user.service';
import { LoaderService } from './loader.service';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class TokenService {

  readonly STORAGE_KEY = 'token';
  readonly STORAGE_KEY_REFRESH_TOKEN = 'refresh_token';
  readonly STORAGE_KEY_LOCAL = 'local_token';
  readonly Login_User_Id = 'LoginUser';
  readonly User_Id = 'LoginUserId';
  readonly user_status = 'user_status';
  readonly Section_Name = 'activeSecName';
  readonly Auth_Token = 'AUTH_TOKEN';
  readonly User_Name = 'user_name';
  readonly REDIRECT_URL = 'REDIRECT_URL';
  readonly cookie_token = 'param';
  readonly BROWSER_SESSION_ID = 'browserSessionId';
  authUrlPath = environment.API_AUTH_PATH;

  protected basePath = 'http://localhost';
  public configuration = new Configuration();
  public encoder: HttpParameterCodec;
  requestId: string;

  //this flag is for updating change pWD screen header and footer
  public getChangePWDScreen: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public getChangePWDScreen$ = this.getChangePWDScreen.asObservable();
  private refreshTokenTimeoutId;

  private permissionUpdateSubject = new BehaviorSubject<string>(null as any);
  public permissionUpdated = this.permissionUpdateSubject.asObservable();
  constructor(private readonly storage: SessionStorageService,
    protected httpClient: HttpClient, private readonly encryptService: EncryptService,
    private readonly tokenManagementService: TokenManagementService,
    @Optional() @Inject(BASE_PATH) basePath: string,
    @Optional() configuration: Configuration,
    private readonly sessionService: AppSessionService,
    private readonly userService: UserService,
    public router: Router,
    private readonly loader: LoaderService
  ) {
    this.getChangePWDScreen.next(true);
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }

  public setToken(access_token: string, refresh_token: string, username: string) {
    this.storage.remove(this.User_Name);
    this.storage.set(this.User_Name, username);
    this.setAccessToken(access_token);
    this.setRefreshToken(refresh_token);
    this.storage.set('IsUserLoggedIn', 'true');

    // Insert user session after successful login
    return this.updateUserSession(access_token).pipe(
      catchError((_err) => {
        console.error('Failed to insert user session', _err);

        // Stop spinner to re-enable login button
        this.loader.showSpinner.next(false);

        // Clear tokens immediately
        this.removeToken();
        this.storage.set('SessionError', 'true');

        // Navigate to home page - message will be shown by login component
        this.router.navigate(['/']);

        return EMPTY;
      })
    );
  }
  public setAccessToken(access_token: string) {
    this.removeAccessToken();
    this.storage.set(this.STORAGE_KEY, access_token);
    this.permissionUpdateSubject.next(access_token)
  }
  public setRefreshToken(refresh_token: string) {
    this.removeRefreshToken();
    this.storage.set(this.STORAGE_KEY_REFRESH_TOKEN, refresh_token);
    this.startOrRestartRefreshTokenTimer();
  }
  public setRedirectUrl(url: string) {
    this.storage.set(this.REDIRECT_URL, url);
  }


  public getToken(): string {
    return this.storage.get(this.STORAGE_KEY);
  }
  public getBrowserSessionId(): string {
    return window?.sessionStorage?.getItem('browserSessionId') ?? null
  }

  public getRefreshToken(): string {
    return this.storage.get(this.STORAGE_KEY_REFRESH_TOKEN);
  }

  public removeAccessToken() {
    this.storage.remove(this.STORAGE_KEY);
  }
  public removeRefreshToken() {
    this.storage.remove(this.STORAGE_KEY_REFRESH_TOKEN);
  }
  public getAuthToken(): string {
    return this.storage.get(this.Auth_Token);
  }

  public setAuthToken(authToken: string) {
    this.storage.set(this.Auth_Token, authToken);
  }

  public setLoggedInUserId(userId: string) {
    if (userId) {
      const encUserId = this.encryptService.encryptString(userId, environment.sessionPropEncKey)
      this.storage.set(this.Login_User_Id, encUserId);
    } else {
      this.storage.set(this.Login_User_Id, userId);
    }
  }
  public setLoggedInUserStatus(userStatus: string) {
    this.storage.set(this.user_status, userStatus);
  }

  public getLoggedInUserStatus(): string {
    return this.storage.get(this.user_status);
  }

  public setSidebarToggleStatus(status: boolean) {
    this.storage.set('SidebarToggleStatus', status);
  }

  public getSidebarToggleStatus(): boolean {
    return this.storage.get('SidebarToggleStatus');
  }

  public getLoggedInUserId(): string {
    if (this.storage.get(this.Login_User_Id)) {
      const login_User_Id = this.encryptService.decryptString(this.storage.get(this.Login_User_Id), environment.sessionPropEncKey);
      return login_User_Id;
    } else {
      return this.storage.get(this.Login_User_Id);
    }
  }
  public getEncryptedValue(): string {
    return this.storage.get(this.Login_User_Id);
  }

  public getUserId(): string {
    return this.storage.get(this.User_Id);
  }

  public isLogged(): boolean {
    if (this.getToken()) {
      return true;
    }

    return false;
  }

  public removeToken(): void {
    this.storage.remove(this.STORAGE_KEY_LOCAL);
    this.removeAccessToken();
    this.storage.remove(this.Login_User_Id);
    this.storage.remove(this.user_status);
    this.storage.remove(this.Section_Name);
    this.storage.remove(this.User_Id);
    this.storage.remove(this.Auth_Token);
    this.removeRefreshToken();
  }

  // Insert/Update user session - call after login or token refresh
  updateUserSession(token?: string) {
    const browserSessionId = this.sessionService.getBrowserSession();
    if (browserSessionId !== null) {
      return this.userService.apicancelLogoutOnBrowserRefreshPost(browserSessionId, token);
    } else {
      return of(false);
    }
  }

  public getParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const paramValue = urlParams.get(this.cookie_token);
    if (paramValue) {
      const decrypted = this.encryptService.decryptString(paramValue, environment.sessionPropEncKey);
      return decrypted ? encodeURIComponent(decrypted) : undefined;
    }
  }
  public delete_cookieDomain() {
    const authURLHost = new URL(this.authUrlPath);
    let cookieDomain = authURLHost.host.substring(authURLHost.host.indexOf('.'));
    cookieDomain = cookieDomain.substring(0, cookieDomain.indexOf(':') === -1 ? cookieDomain.length : cookieDomain.indexOf(':'));
    document.cookie = this.cookie_token + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;domain=' + cookieDomain;
  }

  public acquireRefreshToken(): Observable<TokenResponseDto> {
    const refreshToken = this.getRefreshToken();
    const accessToken = this.getToken();

    if (refreshToken && accessToken) {
      return this.tokenManagementService
        .refreshToken(refreshToken)
        .pipe(
          tap({
            next: (res: TokenResponseDto) => {


              this.removeRefreshToken();
              this.removeAccessToken();
              if (res.refresh_token != null && res.access_token != null) {
                this.setRefreshToken(res.refresh_token);
                this.setAccessToken(res.access_token);
              }
              else {
                (window as any)._sessionExpiredData = {
                  isSessionExpired: true
                };
                this.sessionService.removeBrowserSession();
                this.storage.set('IsUserLoggedIn', 'false');
                // remove user session from session storage
                this.removeToken();
                this.delete_cookieDomain();
                this.router.navigate(['/']);
              }
            }
          })
        );
    } else {
      const defaultResponse = new TokenResponseDto();
      const obs = new BehaviorSubject(defaultResponse);
      obs.complete();
      return obs.asObservable();
    }
  }
  startOrRestartRefreshTokenTimer() {
    if (this.refreshTokenTimeoutId) {
      clearTimeout(this.refreshTokenTimeoutId);
      this.refreshTokenTimeoutId = null;
    }
    const lifespanInSeconds = environment.refreshTokenLifetime ? environment.refreshTokenLifetime : 480;
    const lifespanInMS = lifespanInSeconds * 1000;
    this.refreshTokenTimeoutId = setTimeout(() => this.acquireRefreshToken().subscribe(), lifespanInMS);
  }

  stopRefreshTokenTimer() {
    // This is intentional
    // this.refreshTokenTimeout.clearTimeout();
  }

}
